/**
 * Per-user (cross-device) setup state on the Users row: the top-level
 * `setupState` attribute, written by the user through `updateMe.setupState`.
 * Never `settingsJson` (presence broadcasts it, and partial writes drop keys).
 *
 * The server merges a whitelisted patch: unknown keys are dropped, the tour
 * version only moves forward, and the completion time is stamped server-side.
 */

export type UserSetupState = {
  /** Highest walkthrough (`TUTORIAL_VERSION`) this person finished or skipped, on any device. */
  tourVersionSeen?: number
  /** Explainer markers, e.g. `{ sessionRecording: 'member-v1' }`. */
  explainersSeen?: { sessionRecording?: string }
  /** Server-stamped when a device reports finishing setup. Informational. */
  lastCompletedAt?: string
  lastCompletedFlowVersion?: number
}

/** What a client may send. `lastCompletedAt` is never accepted from a client. */
export type UserSetupStatePatch = {
  tourVersionSeen?: number
  explainersSeen?: { sessionRecording?: string }
  lastCompletedFlowVersion?: number
}

const MAX_TOUR_VERSION = 10_000
const MAX_FLOW_VERSION = 1000
const EXPLAINER_MARKER = /^[a-z][a-z-]{0,30}-v\d{1,3}$/

function parseObject(raw: unknown): Record<string, unknown> | null {
  let value: unknown = raw
  if (typeof value === 'string') {
    if (value.length > 2048) return null
    try {
      value = JSON.parse(value)
    } catch {
      return null
    }
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  return value as Record<string, unknown>
}

function intInRange(value: unknown, min: number, max: number): number | undefined {
  return typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max
    ? value
    : undefined
}

function explainers(value: unknown): { sessionRecording?: string } | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined
  const marker = (value as Record<string, unknown>).sessionRecording
  return typeof marker === 'string' && EXPLAINER_MARKER.test(marker)
    ? { sessionRecording: marker }
    : undefined
}

/** Whitelist a client patch; null when nothing valid is left. */
export function normalizeUserSetupStatePatch(raw: unknown): UserSetupStatePatch | null {
  const obj = parseObject(raw)
  if (!obj) return null
  const patch: UserSetupStatePatch = {}
  const tour = intInRange(obj.tourVersionSeen, 0, MAX_TOUR_VERSION)
  if (tour !== undefined) patch.tourVersionSeen = tour
  const seen = explainers(obj.explainersSeen)
  if (seen) patch.explainersSeen = seen
  const flow = intInRange(obj.lastCompletedFlowVersion, 1, MAX_FLOW_VERSION)
  if (flow !== undefined) patch.lastCompletedFlowVersion = flow
  return Object.keys(patch).length > 0 ? patch : null
}

/** Reads the stored attribute (object or AWSJSON string), keeping only known, valid keys. */
export function parseUserSetupState(raw: unknown): UserSetupState {
  const obj = parseObject(raw)
  if (!obj) return {}
  const state: UserSetupState = {}
  const tour = intInRange(obj.tourVersionSeen, 0, MAX_TOUR_VERSION)
  if (tour !== undefined) state.tourVersionSeen = tour
  const seen = explainers(obj.explainersSeen)
  if (seen) state.explainersSeen = seen
  if (typeof obj.lastCompletedAt === 'string' && Number.isFinite(Date.parse(obj.lastCompletedAt))) {
    state.lastCompletedAt = obj.lastCompletedAt
  }
  const flow = intInRange(obj.lastCompletedFlowVersion, 1, MAX_FLOW_VERSION)
  if (flow !== undefined) state.lastCompletedFlowVersion = flow
  return state
}

export function mergeUserSetupState(
  existingRaw: unknown,
  patch: UserSetupStatePatch,
  nowIso: string,
): UserSetupState {
  const next = parseUserSetupState(existingRaw)
  if (patch.tourVersionSeen !== undefined) {
    next.tourVersionSeen = Math.max(next.tourVersionSeen ?? 0, patch.tourVersionSeen)
  }
  if (patch.explainersSeen?.sessionRecording) {
    next.explainersSeen = { ...next.explainersSeen, sessionRecording: patch.explainersSeen.sessionRecording }
  }
  if (patch.lastCompletedFlowVersion !== undefined) {
    next.lastCompletedFlowVersion = Math.max(next.lastCompletedFlowVersion ?? 0, patch.lastCompletedFlowVersion)
    next.lastCompletedAt = nowIso
  }
  return next
}

/** The walkthrough at `version` was already finished or skipped by this person on some device. */
export function hasSeenTourVersion(state: unknown, version: number): boolean {
  return (parseUserSetupState(state).tourVersionSeen ?? 0) >= version
}
